import React, { useEffect, useState, useContext } from 'react';
import { AuthUserContext } from '../../Session';
import { 
  Button, 
  TextField, 
  Switch, 
  FormControlLabel, 
  Typography, 
  Box, 
  Grid,
  Divider,
  CircularProgress,
  Fade,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import BusinessIcon from '@material-ui/icons/Business';
import EmailIcon from '@material-ui/icons/Email';
import PhoneIcon from '@material-ui/icons/Phone';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import SaveIcon from '@material-ui/icons/Save';
import PeopleIcon from '@material-ui/icons/People';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import DeleteIcon from '@material-ui/icons/Delete';

// Usa firebase compatível com o restante do projeto
import firebase from 'firebase/compat/app';
import 'firebase/compat/database';
import 'firebase/compat/functions';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 600,
    color: '#1e1e2d',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  subtitle: {
    color: '#92929f',
    marginTop: theme.spacing(0.5),
  },
  card: {
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    marginBottom: theme.spacing(3),
  },
  cardHeader: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#ffffff',
    '& .MuiCardHeader-title': {
      fontWeight: 600,
      fontSize: '1.1rem',
    },
  },
  cardContent: {
    padding: theme.spacing(3),
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '&:hover fieldset': {
        borderColor: '#667eea',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#667eea',
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#667eea',
    },
  },
  inputIcon: {
    color: '#92929f',
    marginRight: theme.spacing(1),
  },
  switchContainer: {
    padding: theme.spacing(2),
    borderRadius: '8px',
    backgroundColor: '#f5f8fa',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  saveButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#ffffff',
    padding: theme.spacing(1.5),
    borderRadius: '8px',
    fontWeight: 600,
    fontSize: '1rem',
    textTransform: 'none',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
    '&:hover': {
      background: 'linear-gradient(135deg, #5568d3 0%, #6b3f91 100%)',
      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
    },
    '&:disabled': {
      background: '#e0e0e0',
      color: '#9e9e9e',
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
  },
  successAlert: {
    marginTop: theme.spacing(2),
    borderRadius: '8px',
  },
  staffList: {
    '& .MuiListItem-root': {
      borderRadius: '8px',
      marginBottom: theme.spacing(1),
      backgroundColor: '#f5f8fa',
      '&:hover': {
        backgroundColor: '#e8ecf0',
      },
    },
  },
  addButton: {
    background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
    color: '#ffffff',
    padding: theme.spacing(1.5),
    borderRadius: '8px',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 4px 15px rgba(72, 187, 120, 0.4)',
    '&:hover': {
      background: 'linear-gradient(135deg, #38a169 0%, #2f855a 100%)',
      boxShadow: '0 6px 20px rgba(72, 187, 120, 0.6)',
    },
  },
  deleteButton: {
    color: '#f56565',
    '&:hover': {
      backgroundColor: 'rgba(245, 101, 101, 0.1)',
    },
  },
}));

const OrganizationsAdmin = () => {
  const classes = useStyles();
  const context = useContext(AuthUserContext);
  const user = context?.authUser !== undefined ? context.authUser : context;
  const [form, setForm] = useState({
    name: '',
    email: '',
    notificationEmail: '',
    whatsAppPhoneNumber: '',
    fixedPhoneNumber: '',
    mobilePhoneNumber: '',
    enableOnlineOrder: false
  });
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [staff, setStaff] = useState([]);
  const [openStaffDialog, setOpenStaffDialog] = useState(false);
  const [newStaffEmail, setNewStaffEmail] = useState('');
  const [newStaffName, setNewStaffName] = useState('');
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffSuccess, setStaffSuccess] = useState('');
  const [staffError, setStaffError] = useState('');

  useEffect(() => {
    if (!user || !user.default_organization) return;
    setLoading(true);
    
    // Load organization info
    firebase.database().ref(`organizations/${user.default_organization}/about`).once('value').then(snapshot => {
      const about = snapshot.val() || {};
      setForm({
        name: about.name || '',
        email: about.email || '',
        notificationEmail: about.notificationEmail || '',
        whatsAppPhoneNumber: about.whatsAppPhoneNumber || '',
        fixedPhoneNumber: about.fixedPhoneNumber || '',
        mobilePhoneNumber: about.mobilePhoneNumber || '',
        enableOnlineOrder: about.enableOnlineOrder || false
      });
      setLoading(false);
    });

    // Load staff list
    firebase.database().ref(`organizations/${user.default_organization}/staff`).on('value', snapshot => {
      const staffData = snapshot.val() || {};
      const staffList = Object.keys(staffData).map(key => ({
        id: key,
        ...staffData[key]
      }));
      setStaff(staffList);
    });

    return () => {
      firebase.database().ref(`organizations/${user.default_organization}/staff`).off();
    };
  }, [user]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    if (!user || !user.default_organization) return;
    setLoading(true);
    await firebase.database().ref(`organizations/${user.default_organization}/about`).update(form);
    setLoading(false);
    setSuccess(true);
    setTimeout(() => setSuccess(false), 2000);
  };

  const handleAddStaff = async () => {
    if (!user || !user.default_organization || !newStaffEmail || !newStaffName) return;
    
    setStaffLoading(true);
    setStaffError('');
    setStaffSuccess('');

    try {
      // Add staff to database
      const staffRef = firebase.database().ref(`organizations/${user.default_organization}/staff`).push();
      await staffRef.set({
        name: newStaffName,
        email: newStaffEmail,
        addedAt: firebase.database.ServerValue.TIMESTAMP,
        addedBy: user.email
      });

      // Send invitation email using Firebase Functions
      try {
        const sendInvite = firebase.functions().httpsCallable('sendStaffInvitation');
        await sendInvite({
          organizationId: user.default_organization,
          staffEmail: newStaffEmail,
          staffName: newStaffName,
          organizationName: form.name
        });
        setStaffSuccess('Funcionário adicionado e email de convite enviado!');
      } catch (emailError) {
        console.error('Error sending invitation email:', emailError);
        setStaffSuccess('Funcionário adicionado, mas o email não pôde ser enviado.');
      }

      setNewStaffEmail('');
      setNewStaffName('');
      setOpenStaffDialog(false);
      setTimeout(() => setStaffSuccess(''), 5000);
    } catch (error) {
      console.error('Error adding staff:', error);
      setStaffError(error.message || 'Erro ao adicionar funcionário');
    } finally {
      setStaffLoading(false);
    }
  };

  const handleDeleteStaff = async (staffId, staffEmail) => {
    if (!user || !user.default_organization) return;
    
    // Only organization admin (email in about) can delete staff
    if (user.email !== form.email) {
      setStaffError('Apenas o administrador da organização pode remover funcionários');
      setTimeout(() => setStaffError(''), 3000);
      return;
    }
    
    if (window.confirm('Tem certeza que deseja remover este funcionário?')) {
      try {
        await firebase.database().ref(`organizations/${user.default_organization}/staff/${staffId}`).remove();
        setStaffSuccess('Funcionário removido com sucesso!');
        setTimeout(() => setStaffSuccess(''), 3000);
      } catch (error) {
        console.error('Error deleting staff:', error);
        setStaffError('Erro ao remover funcionário');
      }
    }
  };

  const handleCloseStaffDialog = () => {
    setOpenStaffDialog(false);
    setNewStaffEmail('');
    setNewStaffName('');
    setStaffError('');
  };

  if (loading) {
    return (
      <Box className={classes.root}>
        <div className={classes.loadingContainer}>
          <Box textAlign="center">
            <CircularProgress size={50} style={{ color: '#667eea' }} />
            <Typography variant="h6" style={{ marginTop: 16, color: '#92929f' }}>
              Carregando dados da organização...
            </Typography>
          </Box>
        </div>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography variant="h4" className={classes.title}>
          <BusinessIcon style={{ fontSize: '2rem', color: '#667eea' }} />
          Configurações da Organização
        </Typography>
        <Typography variant="body2" className={classes.subtitle}>
          Gerencie as informações e configurações da sua organização
        </Typography>
      </Box>

      {/* Main Content */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card className={classes.card}>
            <CardHeader 
              className={classes.cardHeader}
              title="Informações Básicas"
            />
            <CardContent className={classes.cardContent}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="flex-end">
                    <BusinessIcon className={classes.inputIcon} />
                    <TextField
                      label="Nome da Organização"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      fullWidth
                      variant="outlined"
                      className={classes.textField}
                      placeholder="Digite o nome da sua organização"
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Box display="flex" alignItems="flex-end">
                    <EmailIcon className={classes.inputIcon} />
                    <TextField
                      label="Email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      fullWidth
                      variant="outlined"
                      className={classes.textField}
                      placeholder="contato@exemplo.com"
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Box display="flex" alignItems="flex-end">
                    <EmailIcon className={classes.inputIcon} />
                    <TextField
                      label="Email de Notificação de Pedidos"
                      name="notificationEmail"
                      type="email"
                      value={form.notificationEmail}
                      onChange={handleChange}
                      fullWidth
                      variant="outlined"
                      className={classes.textField}
                      placeholder="pedidos@exemplo.com"
                      helperText="Este email receberá notificações de novos pedidos online"
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card className={classes.card}>
            <CardHeader 
              className={classes.cardHeader}
              title="Informações de Contato"
            />
            <CardContent className={classes.cardContent}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="flex-end">
                    <WhatsAppIcon className={classes.inputIcon} />
                    <TextField
                      label="WhatsApp"
                      name="whatsAppPhoneNumber"
                      value={form.whatsAppPhoneNumber}
                      onChange={handleChange}
                      fullWidth
                      variant="outlined"
                      className={classes.textField}
                      placeholder="(00) 00000-0000"
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="flex-end">
                    <PhoneIcon className={classes.inputIcon} />
                    <TextField
                      label="Telefone Fixo"
                      name="fixedPhoneNumber"
                      value={form.fixedPhoneNumber}
                      onChange={handleChange}
                      fullWidth
                      variant="outlined"
                      className={classes.textField}
                      placeholder="(00) 0000-0000"
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="flex-end">
                    <PhoneIcon className={classes.inputIcon} />
                    <TextField
                      label="Celular"
                      name="mobilePhoneNumber"
                      value={form.mobilePhoneNumber}
                      onChange={handleChange}
                      fullWidth
                      variant="outlined"
                      className={classes.textField}
                      placeholder="(00) 00000-0000"
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card className={classes.card}>
            <CardHeader 
              className={classes.cardHeader}
              title="Configurações"
            />
            <CardContent className={classes.cardContent}>
              <Box className={classes.switchContainer}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.enableOnlineOrder}
                      onChange={handleChange}
                      name="enableOnlineOrder"
                      style={{ 
                        color: form.enableOnlineOrder ? '#667eea' : undefined 
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" style={{ fontWeight: 500 }}>
                        Pedidos Online
                      </Typography>
                      <Typography variant="caption" style={{ color: '#92929f' }}>
                        Habilitar botão de pedido online no site
                      </Typography>
                    </Box>
                  }
                />
              </Box>
              
              <Divider style={{ margin: '16px 0' }} />
              
              <Button
                variant="contained"
                className={classes.saveButton}
                onClick={handleSave}
                disabled={loading}
                fullWidth
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              >
                {loading ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
              
              <Fade in={success}>
                <Alert severity="success" className={classes.successAlert}>
                  Configurações salvas com sucesso!
                </Alert>
              </Fade>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Staff Management Section */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card className={classes.card}>
            <CardHeader 
              className={classes.cardHeader}
              title="Equipe"
              action={
                <Button
                  className={classes.addButton}
                  startIcon={<PersonAddIcon />}
                  onClick={() => setOpenStaffDialog(true)}
                >
                  Adicionar Funcionário
                </Button>
              }
            />
            <CardContent className={classes.cardContent}>
              {staffSuccess && (
                <Alert severity="success" className={classes.successAlert} style={{ marginBottom: 16 }}>
                  {staffSuccess}
                </Alert>
              )}
              
              {staffError && (
                <Alert severity="error" className={classes.successAlert} style={{ marginBottom: 16 }}>
                  {staffError}
                </Alert>
              )}

              {staff.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <PeopleIcon style={{ fontSize: 64, color: '#92929f', marginBottom: 16 }} />
                  <Typography variant="body1" style={{ color: '#92929f' }}>
                    Nenhum funcionário cadastrado ainda
                  </Typography>
                  <Typography variant="body2" style={{ color: '#92929f', marginTop: 8 }}>
                    Clique em "Adicionar Funcionário" para começar
                  </Typography>
                </Box>
              ) : (
                <List className={classes.staffList}>
                  {staff.map((member) => (
                    <ListItem key={member.id}>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body1" style={{ fontWeight: 500 }}>
                              {member.name}
                            </Typography>
                            <Chip 
                              size="small" 
                              label={member.email} 
                              icon={<EmailIcon style={{ fontSize: '1rem' }} />}
                              style={{ backgroundColor: '#e8ecf0' }}
                            />
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" style={{ color: '#92929f' }}>
                            Adicionado em {member.addedAt ? new Date(member.addedAt).toLocaleDateString('pt-BR') : 'N/A'}
                            {member.addedBy && ` por ${member.addedBy}`}
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction>
                        {user.email === form.email && (
                          <IconButton 
                            edge="end" 
                            className={classes.deleteButton}
                            onClick={() => handleDeleteStaff(member.id, member.email)}
                            title="Remover funcionário"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Staff Dialog */}
      <Dialog open={openStaffDialog} onClose={handleCloseStaffDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PersonAddIcon style={{ color: '#667eea' }} />
            Adicionar Novo Funcionário
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <TextField
              label="Nome Completo"
              value={newStaffName}
              onChange={(e) => setNewStaffName(e.target.value)}
              fullWidth
              variant="outlined"
              className={classes.textField}
              placeholder="João Silva"
              style={{ marginBottom: 16 }}
            />
            <TextField
              label="Email"
              value={newStaffEmail}
              onChange={(e) => setNewStaffEmail(e.target.value)}
              type="email"
              fullWidth
              variant="outlined"
              className={classes.textField}
              placeholder="joao@exemplo.com"
              helperText="Um email de convite será enviado para este endereço"
            />
            
            {staffError && (
              <Alert severity="error" style={{ marginTop: 16, borderRadius: '8px' }}>
                {staffError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button onClick={handleCloseStaffDialog} style={{ color: '#92929f' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleAddStaff}
            disabled={!newStaffName || !newStaffEmail || staffLoading}
            className={classes.addButton}
            startIcon={staffLoading ? <CircularProgress size={20} color="inherit" /> : <PersonAddIcon />}
          >
            {staffLoading ? 'Adicionando...' : 'Adicionar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizationsAdmin;
